import * as React from 'react';
import { useIntl } from 'react-intl';
import { CmdModal, CmdModalFooterButton } from '@commander-services/gui-components';
import TooltipComponent from '../TooltipComponent';
import useAnalytics from '../../hooks/useAnalytics';

interface IOption {
  item: string;
  value: number;
  passed: boolean;
  popup: string | null;
  customerLabel: string;
}

interface IVehicleGroupFormProps {
  closeCallback: () => void;
  responseCallback: (type: string, values: string[]) => void;
  selectedItems: string[];
  options: Record<string, IOption[]>;
}

function VehicleListForm(props: IVehicleGroupFormProps): JSX.Element {
  const { formatMessage: f } = useIntl();
  const [optionsData, setOptionsData] = React.useState<Record<string, IOption[]>>(props.options);
  const [selectedItemsData, setSelectedItemsData] = React.useState<string[]>(props.selectedItems);
  const { trackEvent } = useAnalytics();

  const handleOnSubmit = () => {
    props.responseCallback('vehicleId', selectedItemsData);
    props.closeCallback();
  };

  const buttons: CmdModalFooterButton[] = [
    {
      id: 'close-modal',
      type: 'button',
      className: 'e-button',
      title: f({ id: 'alarms.modal.rule.button.close' }),
      closeCallback: props.closeCallback,
    },
    {
      id: 'vehiclelist-form-submit',
      type: 'button',
      className: 'e-button e-button--gray',
      title: f({ id: 'form.save' }),
      submitCallback: handleOnSubmit,
    },
  ];

  const filterItems = (value: boolean) => {
    const newOptions = JSON.parse(JSON.stringify(props.options));
    if (value === true) {
      Object.keys(newOptions).forEach((title: string) => {
        newOptions[title] = newOptions[title].filter((i: IOption) => i.passed === false);
      });
    }
    setOptionsData(newOptions);
  };

  const setValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    const id = event.currentTarget.getAttribute('data-id');
    const { checked } = event.target;
    trackEvent(['alarm', 'change', 'Alarm vehicle list select checkbox']);
    let data = [...selectedItemsData];
    if (checked) {
      data.push(String(id));
    } else {
      data = data.filter((i: string) => i !== id);
    }
    setSelectedItemsData(data);
  };

  const getFilteredOptions = () => {
    const filteredOptions: Record<string, IOption[]> = {};
    Object.keys(optionsData).forEach((title: string) => {
      filteredOptions[title] = optionsData[title].filter((item: IOption) =>
        selectedItemsData.includes(String(item.value))
      );
    });
    return filteredOptions;
  };

  return (
    <CmdModal
      id="vehiclelist-form-modal"
      title={f({ id: 'alarms.modal.vehicles.title' })}
      footerButtons={buttons}
      modalFooterCheckbox={f({ id: 'alarms.modal.vehciles.show_error_vehicles' })}
      modalFooterCheckboxCallback={filterItems}
      data-cy="vehiclelist-form-modal"
    >
      <div
        style={{ margin: '0 0 7px', maxHeight: '400px', overflow: 'auto' }}
        className="filtered-list"
      >
        {Object.keys(getFilteredOptions()).map((title: string) => {
          return getFilteredOptions()[title].map((item: IOption, index: number) => {
            return (
              <div
                style={{
                  padding: '10px 12px',
                  color: '#555',
                }}
                className="filtered-item"
                key={index}
              >
                <div className="e-checkbox">
                  <label>
                    <input
                      type="checkbox"
                      onChange={setValue}
                      checked={selectedItemsData.includes(String(item.value))}
                      data-id={item.value}
                      data-title={title}
                      data-cy={`vehiclelist-checkbox-${item.value}`}
                    />
                    <span data-cy={`vehiclelist-span-${item.value}`}>
                      {item.item} &nbsp;
                      {item.passed === false &&
                        (item.popup ? (
                          <TooltipComponent
                            id="vehicle-list"
                            display="inline"
                            firstItems={true}
                            overlay={<div style={{ color: '#fff' }}>{item.popup}</div>}
                            key="err"
                            data-cy={`tooltip-vehiclelist-${item.value}`}
                          >
                            <i
                              id={`icon-${item.value}`}
                              className="cmd-icon cmd-icon-exclamation text-primary"
                              data-cy={`tooltip-vehiclelist-icon-${item.value}`}
                            />
                          </TooltipComponent>
                        ) : (
                          <i
                            id={`icon-${item.value}`}
                            className="cmd-icon cmd-icon-exclamation text-primary"
                            data-cy={`icon-vehiclelist-${item.value}`}
                          />
                        ))}
                    </span>
                  </label>
                </div>
              </div>
            );
          });
        })}
        <div style={{ height: '24px' }} />
      </div>
    </CmdModal>
  );
}

export default VehicleListForm;
